/**
 * Typed client for the identity endpoints in
 * kitty-specs/m01-identity-01M2H1TR/contracts/auth-api.md (A.1, A.2, A.4, A.5,
 * SEC.7; FR-021). Built on `api()` from ./api: every call carries
 * `credentials: 'include'` because sessions are HttpOnly cookies the SPA never
 * reads (C-004), and every refusal surfaces as an `ApiError` whose
 * `problem.type` is one of the `PROBLEM` constants re-exported below from
 * `./problems`, which mirrors the type constants of backend
 * `httpapi/problem.go` one for one on the
 * `https://trellis.bloomcredit.io/problems/<type>` scheme
 * (contracts/session-and-gate.md); `problems.test.ts` reads problem.go, so a
 * type added there fails until it is added here.
 *
 * Not in this client: `GET|DELETE /dev/mailbox` (contracts/dev-mailbox.md) is a
 * development fixture; the e2e suite calls it directly.
 *
 * `logout` and `completeReset` answer `204` with no body; they go through
 * `apiNoContent()`, which shares `api()`'s URL, credentials and problem handling
 * (mission 6 T010: this module no longer calls fetch itself, C-005, R16).
 */
import { ApiError, api, apiNoContent } from './api'

/** Problem types, mirrored from backend problem.go; they live in ./problems (mission 6 T012). */
export { PROBLEM, type ProblemType } from './problems'

/** True when `err` is an ApiError carrying the given problem type. */
export function isProblem(err: unknown, type: string): err is ApiError {
  return err instanceof ApiError && err.problem.type === type
}

export interface FieldError {
  field: string
  message: string
}

/** The `errors` list of a `validation-failed` problem; empty for anything else. */
export function fieldErrors(err: unknown): FieldError[] {
  if (!(err instanceof ApiError)) return []
  const errors: unknown = err.problem.errors
  return Array.isArray(errors) ? (errors as FieldError[]) : []
}

// ---------------------------------------------------------------------------
// Types, one per contract request and response.

export interface SignupPolicy {
  business_email_denylist: string[]
  business_email_message: string
  password_min_length: number
  terms_version: string
}

export interface SignupInput {
  email: string
  password: string
  legal_name: string
  reports_today: boolean
  terms_version: string
  accept_terms: true
}

export type TenantStatus =
  | 'pending_verification'
  | 'pending_subscription'
  | 'active'
  | 'read_only'
  | 'cancelled'

export interface SignupResponse {
  tenant_id: string
  status: 'pending_verification'
  next: 'mfa_enrol'
}

export interface LoginInput {
  email: string
  password: string
}

export interface LoginResponse {
  next: 'mfa_enrol' | 'mfa_challenge'
}

export interface CodeInput {
  /** A 6-digit TOTP code, or for the challenge a backup code. */
  code: string
}

export interface MfaEnrolResponse {
  secret: string
  otpauth_uri: string
}

export interface MfaConfirmResponse {
  /** Shown once. Hold in component state only; never in the query cache or the URL. */
  backup_codes: string[]
  next: 'verify' | 'app'
}

export interface MfaChallengeResponse {
  next: 'app'
}

export interface BackupCodesResponse {
  backup_codes: string[]
}

export interface ResetRequestInput {
  email: string
}

export interface ResetRequestResponse {
  status: 'accepted'
}

export interface ResetCompleteInput {
  token: string
  password: string
}

export interface Tenant {
  id: string
  legal_name: string
  contact_email: string
  reports_today: boolean
  status: TenantStatus
  terms_version: string
  terms_accepted_at: string
  mfa_enrolled: boolean
  verified_at: string | null
}

// ---------------------------------------------------------------------------
// Endpoints.

/** GET /v1/public/signup-policy (A.1, T.1) — unauthenticated. */
export function getSignupPolicy(): Promise<SignupPolicy> {
  return api<SignupPolicy>('/public/signup-policy')
}

/** POST /v1/signup (A.1, A.2, T.1, T.5). Sets the prelogin cookie; the browser goes to /mfa. */
export function signup(input: SignupInput): Promise<SignupResponse> {
  return api<SignupResponse>('/signup', { method: 'POST', body: JSON.stringify(input) })
}

/** POST /v1/auth/login (A.5, SEC.7). One fixed 401 body for every failure. */
export function login(input: LoginInput): Promise<LoginResponse> {
  return api<LoginResponse>('/auth/login', { method: 'POST', body: JSON.stringify(input) })
}

/** POST /v1/auth/mfa/enrol (A.4, SEC.7) — prelogin cookie. Calling again rotates the pending secret. */
export function mfaEnrol(): Promise<MfaEnrolResponse> {
  return api<MfaEnrolResponse>('/auth/mfa/enrol', { method: 'POST' })
}

/** POST /v1/auth/mfa/confirm (A.4) — prelogin cookie. Returns the backup codes once. */
export function mfaConfirm(input: CodeInput): Promise<MfaConfirmResponse> {
  return api<MfaConfirmResponse>('/auth/mfa/confirm', { method: 'POST', body: JSON.stringify(input) })
}

/** POST /v1/auth/mfa/challenge (A.4) — challenge cookie. 5 attempts, then the password again. */
export function mfaChallenge(input: CodeInput): Promise<MfaChallengeResponse> {
  return api<MfaChallengeResponse>('/auth/mfa/challenge', { method: 'POST', body: JSON.stringify(input) })
}

/** POST /v1/auth/mfa/backup-codes (A.4) — full session; a current TOTP code re-authenticates. */
export function regenerateBackupCodes(input: CodeInput): Promise<BackupCodesResponse> {
  return api<BackupCodesResponse>('/auth/mfa/backup-codes', { method: 'POST', body: JSON.stringify(input) })
}

/** POST /v1/auth/logout (A.5) — any level. 204; all three cookies cleared. */
export function logout(): Promise<void> {
  return apiNoContent('/auth/logout', { method: 'POST' })
}

/** POST /v1/auth/reset (A.5, NF.9) — unauthenticated. 202 always, identical bytes. */
export function requestReset(input: ResetRequestInput): Promise<ResetRequestResponse> {
  return api<ResetRequestResponse>('/auth/reset', { method: 'POST', body: JSON.stringify(input) })
}

/** POST /v1/auth/reset/complete (A.5) — unauthenticated. 204; every session of the identity ends. */
export function completeReset(input: ResetCompleteInput): Promise<void> {
  return apiNoContent('/auth/reset/complete', { method: 'POST', body: JSON.stringify(input) })
}

/** GET /v1/tenant (A.2) — full session; allowed through the verification gate. */
export function getTenant(): Promise<Tenant> {
  return api<Tenant>('/tenant')
}
